import { useState } from "react";
import { Link } from "wouter";
import { Calendar, FileText, Loader2, ArrowRight, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import { Card, CardStat } from "@/components/ui/Cards";
import { Button } from "@/components/ui/Buttons";
import { ProjectStatusBadge } from "@/components/ui/Badges";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { useProjects, useDeleteProject, useDashboardState } from "@/hooks/useProjects";
import { computeNextStep, type NextStepResult } from "@/lib/nextStep";
import type { Project } from "@/lib/api";

export default function Dashboard() {
  const { data, isLoading, error } = useProjects();
  const dashboardState = useDashboardState();
  const deleteProject = useDeleteProject();
  const [pendingDelete, setPendingDelete] = useState<Project | null>(null);

  // Map projectId → next-step. Computed once per render off the aggregated
  // dashboard-state response (one round-trip for all projects).
  const nextStepByProject = new Map<string, NextStepResult>();
  if (dashboardState.data?.states) {
    for (const s of dashboardState.data.states) {
      nextStepByProject.set(s.projectId, computeNextStep(s));
    }
  }

  if (isLoading) {
    return (
      <Layout>
        <PageHeader title="Dashboard" subtitle="Loading projects…" />
        <div className="flex items-center justify-center py-24">
          <Loader2 size={20} className="animate-spin text-orange-400" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <PageHeader title="Dashboard" subtitle="Couldn't load projects" />
        <div className="p-8">
          <Card>
            <div className="text-sm text-red-400 font-medium">{(error as Error).message}</div>
            <p className="mt-2 text-xs" style={{ color: "#6B7280" }}>
              Verify the api-server is running on http://localhost:8081 and that your Clerk session
              is valid.
            </p>
          </Card>
        </div>
      </Layout>
    );
  }

  const projects = data?.projects ?? [];

  if (projects.length === 0) {
    return (
      <Layout>
        <PageHeader title="Dashboard" />
        <div className="flex flex-col items-center justify-center py-24 gap-6">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ backgroundColor: "rgba(249,115,22,0.1)" }}
          >
            <FileText size={28} style={{ color: "#F97316" }} />
          </div>
          <div className="text-center">
            <h2 className="text-xl font-bold text-white">No projects yet</h2>
            <p className="mt-2 text-sm" style={{ color: "#6B7280" }}>
              Start by creating your first RFP project.
            </p>
          </div>
          <Link href="/new">
            <Button variant="primary">Start New RFP</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  // Top-level stats across the org's portfolio.
  const active = projects.filter((p) => p.status === "active").length;
  const submitted = projects.filter((p) => p.status === "submitted").length;
  const won = projects.filter((p) => p.status === "won").length;

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteProject.mutateAsync(pendingDelete.id);
      toast.success(`Deleted "${pendingDelete.title}".`);
      setPendingDelete(null);
    } catch (err) {
      toast.error((err as Error).message ?? "Couldn't delete the project.");
    }
  };

  return (
    <Layout>
      <PageHeader
        title="Dashboard"
        subtitle={`${projects.length} project${projects.length === 1 ? "" : "s"}`}
        actions={
          <Link href="/new">
            <Button variant="primary" size="sm">
              New RFP
            </Button>
          </Link>
        }
      />

      <div className="p-8 space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <CardStat label="Total" value={projects.length} />
          <CardStat label="Active" value={active} accent={active > 0} />
          <CardStat label="Submitted" value={submitted} />
          <CardStat label="Won" value={won} />
        </div>

        <div>
          <h3 className="text-sm font-semibold text-white mb-3">Projects</h3>
          <div className="space-y-2">
            {projects.map((p) => {
              const deadline = p.submissionDeadline ? new Date(p.submissionDeadline) : null;
              const daysLeft = deadline
                ? Math.ceil((deadline.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
                : null;
              return (
                <div
                  key={p.id}
                  className="rounded-xl border px-4 py-3 flex items-center gap-4 transition-colors hover:border-orange-500/30"
                  style={{
                    backgroundColor: "#1F2933",
                    borderColor: "rgba(229,231,235,0.08)",
                  }}
                >
                  <Link href={`/projects/${p.id}`} className="flex-1 min-w-0 cursor-pointer">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-white truncate">{p.title}</p>
                      <ProjectStatusBadge status={p.status} />
                    </div>
                    <p className="text-xs mt-0.5 truncate" style={{ color: "#6B7280" }}>
                      {p.requesterName}
                    </p>
                    {(() => {
                      const ns = nextStepByProject.get(p.id);
                      if (!ns) return null;
                      const c =
                        ns.tone === "ok" ? "#22c55e" : ns.tone === "muted" ? "#6B7280" : "#F97316";
                      return (
                        <p
                          className="text-[11px] mt-1 truncate inline-flex items-center gap-1.5"
                          style={{ color: c }}
                          title={ns.title}
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full inline-block flex-shrink-0"
                            style={{ backgroundColor: c }}
                          />
                          Next: {ns.short}
                        </p>
                      );
                    })()}
                  </Link>
                  {deadline && (
                    <div className="flex items-center gap-1.5 text-xs flex-shrink-0">
                      <Calendar
                        size={12}
                        style={{
                          color: daysLeft !== null && daysLeft < 14 ? "#ef4444" : "#6B7280",
                        }}
                      />
                      <span
                        style={{
                          color: daysLeft !== null && daysLeft < 14 ? "#ef4444" : "#6B7280",
                        }}
                      >
                        {deadline.toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                        })}
                      </span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setPendingDelete(p);
                    }}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-white/5"
                    aria-label={`Delete project ${p.title}`}
                    title="Delete project"
                  >
                    <Trash2 size={14} />
                  </button>
                  <Link href={`/projects/${p.id}`}>
                    <ArrowRight size={14} style={{ color: "#6B7280" }} />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <ConfirmDeleteDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => {
          if (!o) setPendingDelete(null);
        }}
        title="Delete this project?"
        description="This permanently removes the project, its RFP source, response matrix, drafts, and all uploaded files. This can't be undone."
        requiredPhrase={pendingDelete?.title}
        confirmLabel="Delete project"
        onConfirm={handleConfirmDelete}
        busy={deleteProject.isPending}
      />
    </Layout>
  );
}
